import React, { useState, useRef } from 'react';
import { 
  Alert,
  Typography, 
  Box, 
  Button,
  FormControl,
  Select as MuiSelect,
  MenuItem,
} from '@mui/material';
import Select from 'react-select';
import Grid from '@mui/material/Grid2';
import { useSelector, shallowEqual } from 'react-redux';
import useAlert from '../../alert/useAlert';
import { supabase } from '../../supabaseClient';
import { languages, noteGenerationLanguages, spellingFormats } from '../../constants/languages.js';
import { useLanguagePreferences } from '../../utils/useLanguagePreferences';
import { languagePreferencesForStorage } from '../../utils/languagePreferences';

const LanguageSettings = () => {
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const { setAlert } = useAlert();

  const { settings, loading: loadingSettings, error: settingsError, setPreference, reload } = useLanguagePreferences(userDetails?.id);
  const { input_language: inputLanguage, output_language: outputLanguage, spelling_format: spellingFormat } = settings;
  const [savingOwner, setSavingOwner] = useState(null);
  const loading = savingOwner === userDetails?.id && Boolean(savingOwner);
  const activeOwner = useRef(userDetails?.id);
  activeOwner.current = userDetails?.id;
  const savingRef = useRef(null);
  const cannotEdit = loadingSettings || Boolean(settingsError) || loading || !userDetails?.id;

  const handleSave = async () => {
    if (!userDetails || !userDetails.id) {
      setAlert('User details not found', 'error');
      return;
    }
    
    if (cannotEdit || savingRef.current === userDetails.id) return;
    const owner = userDetails.id;
    savingRef.current = owner;
    setSavingOwner(owner);
    
    try {
      const { error } = await supabase
        .from('datasettings')
        .upsert(
          {
            id: owner,
            ...languagePreferencesForStorage(settings),
            updated_at: new Date(),
          },
          { 
            onConflict: 'id',
          }
        );
        
      if (activeOwner.current !== owner) return;
      if (error) {
        setAlert('Failed to save language settings', 'error');
        console.error('Error saving language settings:', error);
      } else {
        setAlert('Language settings saved successfully', 'success');
      }
    } catch (error) {
      if (activeOwner.current !== owner) return;
      setAlert('An error occurred', 'error');
      console.error('Error saving language settings:', error);
    } finally {
      if (savingRef.current === owner) savingRef.current = null;
      if (activeOwner.current === owner) setSavingOwner(null);
    }
  };

  return (
    <div>
      <Typography variant="h5" sx={{ marginBottom: 3 }}>
        Language Settings
      </Typography>
      {settingsError && <Alert severity="warning" action={<Button onClick={reload}>Retry</Button>}>Language settings could not be loaded. Retry before saving changes.</Alert>}
      <Box sx={{ backgroundColor: '#FFFFFF', borderRadius: '10px', padding: '24px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}>
        {/* First Row - Language Settings */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* First Column - Input Language */}
          <Grid size={{xs: 12, md: 6}} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Default input language
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                The language used for transcribing
              </Typography>
            </Box>
            <FormControl fullWidth>
              <Select
                inputId="default-input-language"
                aria-label="Default input language"
                isDisabled={cannotEdit}
                value={inputLanguage}
                onChange={value => setPreference('input_language', value)}
                options={languages}
                styles={{
                  control: base => ({
                    ...base,
                    border: '1px solid #e0e0e0', // Fixed typo in '1 px solid black'
                    boxShadow: 'none',
                    backgroundColor: '#FFFFFF',
                    minHeight: '50px', // Match MuiSelect height
                    height: '50px', // Explicit height
                    width: '100%'
                  }),
                  container: base => ({
                    ...base,
                    width: '100%'
                  }),
                  // Add these to improve alignment of internal elements
                  valueContainer: base => ({
                    ...base,
                    padding: '2px 8px'
                  }),
                  input: base => ({
                    ...base,
                    margin: '0px',
                    padding: '0px'
                  })
                }}
              />
            </FormControl>
          </Grid>

          {/* Second Column - Output Language */}
          <Grid size={{xs: 12, md: 6}} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Default output language
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                The language used for notes & documents
              </Typography>
            </Box>
            <FormControl fullWidth>
              <MuiSelect
                inputProps={{ 'aria-label': 'Default output language' }}
                disabled={cannotEdit}
                value={outputLanguage}
                onChange={(e) => setPreference('output_language', e.target.value)}
                displayEmpty
                sx={{ height: '50px' }}
              >
                {noteGenerationLanguages.map((lang) => (
                  <MenuItem key={`output-${lang}`} value={lang}>{lang}</MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </Grid>
        </Grid>

        {/* Second Row - Spelling Format */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Spelling
          </Typography>
          <FormControl fullWidth>
            <MuiSelect
              inputProps={{ 'aria-label': 'Spelling' }}
              disabled={cannotEdit}
              value={spellingFormat}
              onChange={(e) => setPreference('spelling_format', e.target.value)}
              displayEmpty
              sx={{ height: '50px' }}
            >
              {spellingFormats.map((format) => (
                <MenuItem key={format} value={format}>{format}</MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Box>

        {/* Save Button */}
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            sx={{
              width: '100%', 
              height: '50px', 
              borderRadius: 30, 
              backgroundColor: '#29C0BB',
              '&:hover': { backgroundColor: '#1BA8A3' }, 
              border: 0
            }}
            onClick={handleSave}
            disabled={cannotEdit}
            data-track-id="settings.language.save"
          >
            <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>
              {loading ? 'Saving...' : 'Save'}
            </Typography>
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default LanguageSettings;